import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Player } from '../services/playerService';

interface PlayerContextType {
  selectedPlayer: Player | null;
  isDialogOpen: boolean;
  openPlayerDialog: (player: Player) => void;
  closePlayerDialog: () => void;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const usePlayer = () => {
  const context = useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return context;
};

interface PlayerProviderProps {
  children: ReactNode;
}

export const PlayerProvider: React.FC<PlayerProviderProps> = ({ children }) => {
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openPlayerDialog = useCallback((player: Player) => {
    setSelectedPlayer(player);
    setIsDialogOpen(true);
  }, []);

  const closePlayerDialog = useCallback(() => {
    setIsDialogOpen(false);
    // Clear selected player after dialog closes to prevent stale data
    setTimeout(() => setSelectedPlayer(null), 300);
  }, []);

  const contextValue = React.useMemo(() => ({
    selectedPlayer,
    isDialogOpen,
    openPlayerDialog,
    closePlayerDialog,
  }), [selectedPlayer, isDialogOpen, openPlayerDialog, closePlayerDialog]);

  return (
    <PlayerContext.Provider value={contextValue}>
      {children}
    </PlayerContext.Provider>
  );
};

